import * as Label from "@radix-ui/react-label";
import { getColumnClasses } from "../../lib/viewportMapper";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import s from "./Checkbox.module.css";
import { FC, useContext } from "react";
import { Icon } from "../Icon";
import { Field, useField } from "formik";
import { validateField } from "@/lib/formValidation";
import { NewsletterContext } from "pages/newsletter";

export type CheckboxProps = {
  identifier: string;
  label: string;
  name?: string;
  properties?: any;
  onCheckedChange?: (value: boolean) => void;
  checked?: boolean;
  value?: string | number;
  className?: string;
  disabled?: boolean;
};
export const Checkbox: FC<CheckboxProps> = ({
  identifier,
  label,
  name = "",
  properties = {},
  onCheckedChange = () => {},
  checked = false,
  value,
  className = "",
  disabled = false,
}) => {
  const { gridColumnClassAutoConfiguration = {}, elementClassAttribute = "" } =
    properties;
  const { viewPorts = {} } = gridColumnClassAutoConfiguration;

  return (
    <div
      className={`${getColumnClasses(viewPorts)} ${elementClassAttribute} ${
        s.CheckboxContainer
      } ${className}`}
    >
      <CheckboxPrimitive.Root
        defaultChecked
        disabled={disabled}
        name={name}
        id={identifier}
        className={`${s.Checkbox} w-[24px] h-[24px] shrink-0  ${
          disabled ? "opacity-50  pointer-events-none" : ""
        }`}
        onCheckedChange={onCheckedChange}
        checked={checked}
        value={value}
      >
        <CheckboxPrimitive.Indicator className="w-[14px]">
          <Icon type="checkmark" className={`${s.CheckboxIndicator} `} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <Label.Root
        className={`${s.CheckboxLabel} ${
          disabled ? "opacity-40  pointer-events-none" : ""
        }`}
        htmlFor={identifier}
        dangerouslySetInnerHTML={{ __html: label }}
      />
    </div>
  );
};

export const Typo3Checkbox = ({
  identifier,
  label,
  name,
  validators,
  value,
  disabled = false,
}) => {
  const [field, meta, helpers] = useField(`['${name}']`);

  const changeHandler = (val) => {
    helpers.setValue(val ? 1 : 0);
  };

  return (
    <Field {...field} validate={(value) => validateField(value, validators)}>
      {({ field, form, meta }) => {
        return (
          <>
            <Checkbox
              identifier={identifier}
              label={label}
              name={field.name}
              checked={!!field.value}
              onCheckedChange={changeHandler}
              className={`${meta.touched && meta.error && "[&>*]:!text-alert"}`}
            />
            {meta.touched && meta.error && (
              <div className={s.ErrorMessage}>
                <Icon
                  type="attention"
                  className="md:w-[16px] w-9 inline-flex mr-4"
                />
                <span>{meta.error}</span>
              </div>
            )}
          </>
        );
      }}
    </Field>
  );
};

export const Typo3Multicheckbox = ({
  name,
  properties,
  identifier,
  validators,
}) => {
  return (
    <div>
      {Object.entries(properties.options).map(([value, label]) => (
        <Typo3Checkbox
          name={name}
          value={value}
          key={value}
          identifier={identifier}
          label={label}
          validators={validators}
        />
      ))}
    </div>
  );
};

export const NewsletterMultiCheckbox = ({ name }) => {
  const [field, , helpers] = useField(`['${name}']`);
  const { value: selectedValues } = field;
  const { setValue } = helpers;
  const { setActiveStage } = useContext(NewsletterContext);

  const handleChange = (value, checked) => {
    if (checked) {
      setValue([...selectedValues, value]);
      setActiveStage([...selectedValues, value]);
    } else {
      setValue(selectedValues.filter((v) => v !== value));
      setActiveStage(selectedValues.filter((v) => v !== value));
    }
  };

  return (
    <div className="flex flex-row flex-wrap">
      <p className="block w-full mb-4 text-gray-700">
        Ich interessiere mich für:
      </p>
      <label className="lg:w-1/3 w-full">
        <input
          className="w-6 h-6"
          type="checkbox"
          value="nfz"
          checked={selectedValues.includes("nfz")}
          onChange={(e) => handleChange("nfz", e.target.checked)}
        />
        <span className="ml-4 text-2xl">Nutzfahrzeuge</span>
      </label>
      <label className="lg:w-1/3 w-full lg:mt-0 mt-4">
        <input
          className="w-6 h-6"
          type="checkbox"
          value="pkw"
          checked={selectedValues.includes("pkw")}
          onChange={(e) => handleChange("pkw", e.target.checked)}
        />
        <span className="ml-4 text-2xl">PKW</span>
      </label>
    </div>
  );
};
